/**
 * Agent voice catalog for script settings (IDs + language / accent / provider tags).
 * ElevenLabs IDs are used for browser / ElevenLabs TTS paths; Kokoro IDs for English phone stacks;
 * Cartesia UUIDs for multilingual phone TTS.
 */

export const VOICE_PROVIDER = {
  ELEVENLABS: "ElevenLabs",
  KOKORO: "Kokoro",
  CARTESIA: "Cartesia",
  FISH: "Fish Audio",
  OTHER: "Other",
};

const CARTESIA_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Default Cartesia voice when a Kokoro (English-only) voice is used with a non-English language. */
export const CARTESIA_DEFAULT_VOICE_ID = "db6b0ed5-d5d3-463d-ae85-518a07d3c2b4"; // Skylar

const KOKORO_VOICE_IDS = new Set([
  "af_heart",
  "af_bella",
  "am_adam",
  "af_sarah",
  "af_emma",
  "am_alex",
  "af_nicole",
  "am_george",
]);

/** @param {string} [id] */
export function inferVoiceProvider(id) {
  if (!id || typeof id !== "string") return VOICE_PROVIDER.OTHER;
  const v = id.trim();
  if (v.startsWith("fish:")) return VOICE_PROVIDER.FISH;
  if (KOKORO_VOICE_IDS.has(v)) return VOICE_PROVIDER.KOKORO;
  if (CARTESIA_UUID_RE.test(v)) return VOICE_PROVIDER.CARTESIA;
  if (v.includes("_") && v.length < 25) return VOICE_PROVIDER.KOKORO;
  if (/^[a-zA-Z0-9]{20,}$/.test(v)) return VOICE_PROVIDER.ELEVENLABS;
  return VOICE_PROVIDER.OTHER;
}

export const AGENT_VOICE_OPTIONS = [
  // ElevenLabs (verified id -> name)
  { id: "9BWtsMINqrJLrRacOk9x", name: "Aria", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "CwhRBWXzGAHq8TQ4Fs17", name: "Roger", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Sarah", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "FGY2WhTYpPnrIDTdsKH5", name: "Laura", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "IKne3meq5aSn9XLyUdCD", name: "Charlie", language: "English (AU)", accent: "Australian", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "JBFqnCBsd6RMkjVDRZzb", name: "George", language: "English (UK)", accent: "British", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "N2lVS1Mw4BFM33nRFxWJ", name: "Callum", language: "English", accent: "Transatlantic", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "SAz9YHcvj6GT2YYXdXww", name: "River", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "TX3LPaxmHKjJ54mW9nSG", name: "Liam", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "XB0fDUnXU5powFXDhCwa", name: "Charlotte", language: "English", accent: "Swedish", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "Xb7hH8MSUJpSbSDYk0k2", name: "Alice", language: "English (UK)", accent: "British", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "XrExE9yKIg1WjnnlVkGX", name: "Matilda", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "bIHbv24MWmeRgasZH58o", name: "Will", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "cgSgspJ2msm6clMCkdW9", name: "Jessica", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "cjVigY5qzO86Huf0OWal", name: "Eric", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "iP95p4xoKVk53go51XdK", name: "Chris", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "nPczCjzI2devNBz1zQrb", name: "Brian", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "onwK4e9ZLuTAKqWW03F9", name: "Daniel", language: "English (UK)", accent: "British", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "pFZP5JQG7iQjIQuC4Bku", name: "Lily", language: "English (UK)", accent: "British", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "pqHfZKP75CvOlQylNhV4", name: "Bill", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "21m00Tcm4TlvDq8ikWAM", name: "Rachel", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "AZnzlk1XvdvUeBnXmlld", name: "Domi", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "ErXwobaYiN019PkySvjV", name: "Antoni", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  { id: "pNInz6obpgDQGcFmaJgB", name: "Adam", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.ELEVENLABS },
  // Kokoro (phone / Kokoro-VAD stacks)
  { id: "af_heart", name: "Heart", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.KOKORO },
  { id: "af_bella", name: "Bella", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.KOKORO },
  { id: "am_adam", name: "Adam", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.KOKORO },
  { id: "af_sarah", name: "Sarah", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.KOKORO },
  { id: "af_emma", name: "Emma", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.KOKORO },
  { id: "am_alex", name: "Alex", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.KOKORO },
  { id: "af_nicole", name: "Nicole", language: "English (US)", accent: "American", provider: VOICE_PROVIDER.KOKORO },
  { id: "am_george", name: "George", language: "English (UK)", accent: "British", provider: VOICE_PROVIDER.KOKORO },
  // Cartesia multilingual (same voice can speak the script language)
  { id: "db6b0ed5-d5d3-463d-ae85-518a07d3c2b4", name: "Skylar", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.CARTESIA, multilingual: true },
  { id: "47c38ca4-5f35-497b-b1a3-415245fb35e1", name: "Daniel", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.CARTESIA, multilingual: true },
  { id: "62ae83ad-4f6a-430b-af41-a9bede9286ca", name: "Gemma", language: "Multilingual", accent: "British", provider: VOICE_PROVIDER.CARTESIA, multilingual: true },
  { id: "30894953-bcce-41fe-892c-15ce19c843ff", name: "Parker", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.CARTESIA, multilingual: true },
  { id: "f6ff7c0c-e396-40a9-a70b-f7607edb6937", name: "Emma", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.CARTESIA, multilingual: true },
  { id: "9626c31c-bec5-4cca-baa8-f8ba9e84c8bc", name: "Jacqueline", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.CARTESIA, multilingual: true },
  { id: "ef191366-f52f-447a-a398-ed8c0f2943a1", name: "Archie", language: "Multilingual", accent: "British", provider: VOICE_PROVIDER.CARTESIA, multilingual: true },
  // Fish Audio (clone / library — ids are fish.audio model reference IDs, prefixed fish:)
  { id: "fish:933563129e564b19a115bedd57b7406a", name: "Sarah", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.FISH, multilingual: true },
  { id: "fish:802e3bc2b27e49c2995d23ef70e6ac89", name: "Energetic Male", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.FISH, multilingual: true },
  { id: "fish:bf322df2096a46f18c579d0baa36f41d", name: "Adrian", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.FISH, multilingual: true },
  { id: "fish:536d3a5e000945adb7038665781a4aca", name: "Ethan", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.FISH, multilingual: true },
  { id: "fish:d8a1340984ee4b63ad1ffae27a6a4339", name: "Elite", language: "Multilingual", accent: "American", provider: VOICE_PROVIDER.FISH, multilingual: true },
];

export const LANGUAGE_CODE_LABELS = {
  en: "English (US)",
  "en-us": "English (US)",
  "en-gb": "English (UK)",
  "en-au": "English (AU)",
  "en-in": "English (IN)",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  pl: "Polish",
  hi: "Hindi",
  ja: "Japanese",
  zh: "Chinese",
  ko: "Korean",
};

/** @param {string} [code] */
export function languageCodeToLabel(code) {
  if (!code || typeof code !== "string") return "English (US)";
  const key = code.trim().toLowerCase();
  return LANGUAGE_CODE_LABELS[key] || code;
}

/**
 * @param {{ name?: string, language?: string, accent?: string, provider?: string }} voice
 * @returns {string}
 */
export function formatAgentVoiceLabel(voice) {
  if (!voice?.name) return "";
  const tags = [voice.language, voice.accent, voice.provider].filter(Boolean);
  return tags.length ? `${voice.name} (${tags.join(" · ")})` : voice.name;
}

/**
 * Merge API voice row with known catalog metadata.
 * @param {{ id: string, name?: string, language?: string, accent?: string, category?: string }} apiVoice
 */
/**
 * Whether a catalog voice can speak the selected script language.
 * Cartesia voices are multilingual; Kokoro is English-only.
 */
export function voiceSupportsLanguage(voice, languageCode) {
  if (!voice) return false;
  const base = String(languageCode || "en").split("-")[0].toLowerCase();
  const provider = voice.provider || inferVoiceProvider(voice.id);
  if (provider === VOICE_PROVIDER.CARTESIA || voice.multilingual) {
    if (Array.isArray(voice.languages) && voice.languages.length) {
      return voice.languages.some((l) => String(l).toLowerCase() === base || String(l).toLowerCase() === String(languageCode).toLowerCase());
    }
    return true;
  }
  if (provider === VOICE_PROVIDER.KOKORO) return base === "en";
  const vlang = String(voice.language || "en").toLowerCase();
  return vlang === base || vlang.startsWith(base + "-") || (vlang.includes("english") && base === "en");
}

export function resolveAgentVoice(apiVoice) {
  const known = AGENT_VOICE_OPTIONS.find((o) => o.id === apiVoice.id);
  const language = known?.language || languageCodeToLabel(apiVoice.language);
  const accent =
    known?.accent ||
    apiVoice.accent ||
    (apiVoice.name && /miranda|southern/i.test(apiVoice.name) ? "Southern" : "American");
  const provider =
    known?.provider ||
    apiVoice.provider ||
    inferVoiceProvider(apiVoice.id);
  return {
    id: apiVoice.id,
    name: apiVoice.name || known?.name || apiVoice.id,
    language,
    accent,
    provider,
  };
}
